import { useCallback } from 'react';
import { useGetActiveSequenceMetadataLazyQuery } from '@frameio/shared/integrations/gql/queries/generated/GetActiveSequenceMetadata';
import { convertSecondsToMicroseconds } from '@frameio/utils/media/media';
import { useDocumentMarkersMigrationQuery } from '../../../../../shared/hooks/markerSync/migration/useDocumentMarkersMigrationQuery';
import {
  type ExportRange,
  isFiniteNumber,
  useExportMarkersAsComments,
} from '../../../../../shared/hooks/useCreateCommentsFromMarkers';
import { POINT_NOT_SET_VALUE } from '../../../../constants';
import type { ExportProgressItem } from '../../../../types/exportProgressTypes';

/**
 * A sequence in/out point is "set" when it is a finite number that isn't the
 * Premiere not-set sentinel. `null` means "first/last frame of the sequence",
 * which is treated as not-set for range-clipping purposes.
 */
const isInOrOutPointSet = (point: number | undefined | null): point is number =>
  isFiniteNumber(point) && point !== POINT_NOT_SET_VALUE;

/**
 * Builds the export-range window for the shared hook from Premiere's
 * `inPoint` / `outPoint` (seconds, with Premiere sentinels). Returns:
 * - `null` when the export covers the full sequence (either explicit
 *   `fullSequence` flag, or both points unset).
 * - An `ExportRange` in microseconds otherwise. When only one side is set,
 *   the other side is opened up: `0` for missing in-point and
 *   `Number.POSITIVE_INFINITY` for missing out-point. This preserves the
 *   "in-only" / "out-only" / "both" branches the legacy code supported.
 */
const exportRange = ({
  fullSequence,
  inPoint,
  outPoint,
}: {
  fullSequence: boolean;
  inPoint: number | undefined | null;
  outPoint: number | undefined | null;
}): ExportRange | null => {
  if (fullSequence) return null;

  const inSet = isInOrOutPointSet(inPoint);
  const outSet = isInOrOutPointSet(outPoint);
  if (!inSet && !outSet) return null;

  const startMicroseconds = inSet
    ? convertSecondsToMicroseconds(inPoint) ?? 0
    : 0;
  const endMicroseconds = outSet
    ? convertSecondsToMicroseconds(outPoint) ?? Number.POSITIVE_INFINITY
    : Number.POSITIVE_INFINITY;

  return { startMicroseconds, endMicroseconds };
};

/**
 * React hook that creates Frame.io comments from Premiere Pro sequence markers.
 *
 * Fetches the active sequence's in/out points, retrieves markers (already in
 * microseconds via {@link useDocumentMarkersMigrationQuery}), and delegates
 * filter+map+send+log to {@link useExportMarkersAsComments}. Range handling
 * supports full-sequence exports, in-only, out-only, and both in+out exports —
 * matching the legacy behavior.
 */
export function useCreateCommentsFromMarkers() {
  const exportMarkersAsComments = useExportMarkersAsComments();
  const { getDocumentMarkers } = useDocumentMarkersMigrationQuery();

  const [getActiveSequenceMetadata] = useGetActiveSequenceMetadataLazyQuery({
    context: { resolveWithUXP: true },
    fetchPolicy: 'network-only',
  });

  const createCommentsFromMarkers = useCallback(
    async (exportProgressItem: ExportProgressItem) => {
      const assetId = exportProgressItem.assetId ?? '';
      if (!assetId) return;

      const { data: sequenceMetadataData } = await getActiveSequenceMetadata();
      const inPoint =
        sequenceMetadataData?.activeProject?.activeSequence?.inPoint;
      const outPoint =
        sequenceMetadataData?.activeProject?.activeSequence?.outPoint;

      const markers = await getDocumentMarkers({
        sequenceId: exportProgressItem.sequenceId,
      });

      await exportMarkersAsComments({
        assetId,
        documentId: exportProgressItem.sequenceId,
        markers,
        range: exportRange({
          fullSequence: !!exportProgressItem.fullSequence,
          inPoint,
          outPoint,
        }),
      });
    },
    [exportMarkersAsComments, getActiveSequenceMetadata, getDocumentMarkers]
  );

  return { createCommentsFromMarkers };
}

export const testExports = {
  exportRange,
  isInOrOutPointSet,
};
